import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IColumnMapping, IRow, TInvalidRows, TParsedData } from '../../../../types/addressBook';

interface IProps {
  dataAddresses: TParsedData[];
  mappedColumns: IColumnMapping[];
  validateRows: (rows: IRow[]) => TInvalidRows;
}

export const useRows = ({ dataAddresses, mappedColumns, validateRows }: IProps) => {
  const [rows, setRows] = useState<IRow[]>([]);
  const deletedRowsStackRef = useRef<IRow[]>([]);

  // Keep the latest rows value in a ref to avoid stale closure issues
  const rowsRef = useRef<IRow[]>(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const handleAddressesToRows = useCallback((): IRow[] => {
    return dataAddresses.map((address, index) => {
      const id = index.toString();
      const newRow: IRow = { id };

      mappedColumns.forEach((column) => {
        const mappedValue = address[column.mappedTo];
        newRow[column.id] = mappedValue ?? '';
      });

      return newRow;
    });
  }, [dataAddresses, mappedColumns]);

  useEffect(() => {
    if (!dataAddresses?.length) {
      setRows([]);
      return;
    }

    const handledRows: IRow[] = handleAddressesToRows();

    setRows(handledRows);
  }, [dataAddresses, handleAddressesToRows]);

  const invalidRows = useMemo(() => validateRows(rows), [rows, validateRows]);
  const hasRows = useMemo(() => rows.length > 0, [rows]);

  return {
    rows,
    setRows,
    deletedRowsStackRef,
    invalidRows,
    hasRows,
    rowsRef,
  };
};
